// Full CELPIP mock sitting: Listening and Reading run set-by-set under section
// rules with NO review between sections; results and CLB practice estimates
// appear only at the end, then the sitting hands off to the form's Writing and
// Speaking tasks. Estimates are explicitly practice estimates, never official.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, BookOpen, CheckCircle2, Clock, Headphones, Mic, PenLine, XCircle } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import DiagramCard from '@/components/DiagramCard';
import { mockLabel } from '@/lib/labels';
import { cn } from '@/lib/utils';

const PHASE = { LOAD: 'LOAD', OVERVIEW: 'OVERVIEW', SECTION_INTRO: 'SECTION_INTRO', AUDIO: 'AUDIO', RUN: 'RUN', RESULTS: 'RESULTS', ERROR: 'ERROR' };
const GRADED_SECTIONS = ['listening', 'reading'];

// The real CELPIP order and shape, for the opening overview. Listening and
// Reading run here as a timed, no-review sitting (auto-scored); Writing and
// Speaking follow as their own tasks after the results.
const COMPONENTS = [
  { key: 'listening', label: 'Listening', icon: Headphones, parts: '6 parts', time: 'about 47-55 min', note: 'Each recording plays once. Auto-scored.' },
  { key: 'reading', label: 'Reading', icon: BookOpen, parts: '4 parts', time: 'about 55-60 min', note: 'Passages, then questions. Auto-scored.' },
  { key: 'writing', label: 'Writing', icon: PenLine, parts: '2 tasks', time: 'about 53-60 min', note: 'An email and a survey response. AI-graded.' },
  { key: 'speaking', label: 'Speaking', icon: Mic, parts: '8 tasks', time: 'about 15-20 min', note: 'Recorded spoken responses. AI-graded.' },
];

const SECTION_META = {
  listening: { label: 'Listening', icon: Headphones, perSetSec: 300 },
  reading: { label: 'Reading', icon: BookOpen, perSetSec: 600 },
};

// Practice estimate bands by accuracy. Clearly labeled estimates: Paragon does
// not publish raw-score conversions, so these are calibration bands for
// practice guidance only.
function estimateBand(pct) {
  if (pct >= 0.92) return '10+';
  if (pct >= 0.84) return '9';
  if (pct >= 0.74) return '8';
  if (pct >= 0.63) return '7';
  if (pct >= 0.5) return '6';
  if (pct >= 0.37) return '5';
  return 'below 5';
}

function formatTime(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function MockRunner() {
  const { exam, form } = useParams();
  const [phase, setPhase] = useState(PHASE.LOAD);
  const [mock, setMock] = useState(null);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [setIdx, setSetIdx] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [picked, setPicked] = useState({});
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFailed, setAudioFailed] = useState(false); // true only when the clip truly can't play (missing file, network / decoder error). Only branch that exposes a Continue-to-questions escape hatch so students are never permanently locked out.
  const [autoplayBlocked, setAutoplayBlocked] = useState(false); // true when the browser blocked autoplay (iOS Safari policy) but the clip DID load — surfaces Tap-to-play, no exam-integrity bypass.
  const [sectionResults, setSectionResults] = useState({});
  const [error, setError] = useState('');
  const answersRef = useRef({});
  const phaseRef = useRef(PHASE.LOAD);
  const audioElRef = useRef(null);
  const audioAdvancedRef = useRef(false);

  const gradedSections = mock ? GRADED_SECTIONS.filter((s) => mock.sections[s]?.length) : [];
  const section = gradedSections[sectionIdx];
  const sets = section ? mock.sections[section] : [];
  const currentSet = sets[setIdx];

  useEffect(() => {
    api.mockForm(exam, form)
      .then((res) => {
        setMock(res.data);
        setPhase(PHASE.OVERVIEW);
        phaseRef.current = PHASE.OVERVIEW;
      })
      .catch((err) => {
        setError(err.response?.data?.detail || 'Mock not available.');
        setPhase(PHASE.ERROR);
      });
  }, [exam, form]);

  const finishSection = useCallback(async () => {
    try {
      const res = await api.mockSectionSubmit(exam, {
        form: Number(form),
        section,
        answers: answersRef.current,
      });
      setSectionResults((r) => ({ ...r, [section]: res.data }));
      answersRef.current = {};
      setPicked({});
      setSetIdx(0);
      setQIdx(0);
      if (sectionIdx + 1 < gradedSections.length) {
        setSectionIdx((i) => i + 1);
        setPhase(PHASE.SECTION_INTRO);
        phaseRef.current = PHASE.SECTION_INTRO;
      } else {
        setPhase(PHASE.RESULTS);
        phaseRef.current = PHASE.RESULTS;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Section submission failed.');
      setPhase(PHASE.ERROR);
    }
  }, [exam, form, section, sectionIdx, gradedSections.length]);

  const advanceSet = useCallback(() => {
    if (setIdx + 1 < sets.length) {
      setSetIdx((i) => i + 1);
      setQIdx(0);
      startSet(sets[setIdx + 1]); // eslint-disable-line no-use-before-define
    } else {
      finishSection();
    }
  }, [setIdx, sets, finishSection]); // eslint-disable-line react-hooks/exhaustive-deps

  const startSet = useCallback(async (theSet) => {
    setSecondsLeft(SECTION_META[theSet.section]?.perSetSec || 300);
    setAudioFailed(false);
    setAutoplayBlocked(false);
    audioAdvancedRef.current = false;
    if (theSet.stimulus?.audio_ref) {
      try {
        const res = await api.contentAudioBlob(theSet.stimulus.audio_ref);
        setAudioUrl(URL.createObjectURL(res.data));
        setPhase(PHASE.AUDIO);
        phaseRef.current = PHASE.AUDIO;
        return;
      } catch {
        // audio_ref declared but the clip couldn't load: fall through to RUN
        // so the transcript (surfaced via audioFailed in showPassage) is what
        // the student sees. Better than a stranded audio screen.
        setAudioFailed(true);
      }
    }
    setPhase(PHASE.RUN);
    phaseRef.current = PHASE.RUN;
  }, []);

  // Advance from the audio screen exactly once (onEnded/onError races +
  // manual "Continue" clicks all funnel through here).
  const advanceFromAudio = useCallback(() => {
    if (audioAdvancedRef.current) return;
    audioAdvancedRef.current = true;
    setPhase(PHASE.RUN);
    phaseRef.current = PHASE.RUN;
  }, []);

  // Imperative playback the moment the AUDIO phase mounts. Distinguishes:
  //   • NotAllowedError → autoplay policy block (iOS Safari, hardened Chrome).
  //     Clip is loaded; a user gesture will unblock it. Flip `autoplayBlocked`
  //     so we render Tap-to-play — NOT a skip-to-questions button. Mock is a
  //     graded assessment; students must not be able to bypass audio.
  //   • Anything else → the clip genuinely can't play (decoder / network fail).
  //     Flip `audioFailed`, which surfaces the transcript fallback + Continue
  //     button so the student is never permanently locked out.
  useEffect(() => {
    if (phase !== PHASE.AUDIO || !audioUrl) return undefined;
    const el = audioElRef.current;
    if (!el) return undefined;
    let cancelled = false;
    const play = async () => {
      try { await el.play(); }
      catch (err) {
        if (cancelled) return;
        if (err && err.name === 'NotAllowedError') setAutoplayBlocked(true);
        else setAudioFailed(true);
      }
    };
    play();
    return () => { cancelled = true; };
  }, [phase, audioUrl]);

  // User-gesture retry: when the student taps the button after autoplay was
  // blocked. Playback under a user gesture bypasses the policy for this
  // element; a subsequent failure indicates a true media error.
  const tapToPlay = useCallback(async () => {
    const el = audioElRef.current;
    if (!el) return;
    try {
      await el.play();
      setAutoplayBlocked(false);
    } catch (err) {
      if (err && err.name === 'NotAllowedError') return;
      setAutoplayBlocked(false);
      setAudioFailed(true);
    }
  }, []);

  // per-set timer during RUN
  useEffect(() => {
    if (phase !== PHASE.RUN) return undefined;
    const interval = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(interval);
          advanceSet();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [phase, advanceSet]);

  const pick = (setId, qid, oi) => {
    setPicked((p) => ({ ...p, [`${setId}:${qid}`]: oi }));
    const list = answersRef.current[setId] || [];
    const without = list.filter((r) => r.qid !== qid);
    answersRef.current[setId] = [...without, { qid, answer_index: oi }];
  };

  if (phase === PHASE.ERROR) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-100">
        <p className="text-slate-600">{error}</p>
        <Link to={`/${exam}/mocks`} className="text-sm font-medium text-blue-800 hover:underline">Back to mock tests</Link>
      </div>
    );
  }
  if (phase === PHASE.LOAD || !mock) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-100 text-slate-500">Assembling your mock…</div>;
  }

  const Header = ({ showTimer }) => {
    const Icon = SECTION_META[section]?.icon || Headphones;
    return (
      <header className="bg-blue-900 text-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <Icon className="h-4 w-4 opacity-80" />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-blue-200">
                {exam.toUpperCase()} mock test {form} • {SECTION_META[section]?.label || 'Results'}
              </p>
              {phase === PHASE.RUN && currentSet && (
                <p className="text-sm font-semibold">
                  Part {setIdx + 1} of {sets.length}
                </p>
              )}
            </div>
          </div>
          {showTimer && (
            <span
              className={cn(
                'flex items-center gap-1.5 rounded bg-blue-950/60 px-2.5 py-1 text-sm font-semibold tabular-nums',
                secondsLeft <= 30 && 'bg-red-600'
              )}
              data-testid="mock-timer"
            >
              <Clock className="h-3.5 w-3.5" />
              {formatTime(secondsLeft)}
            </span>
          )}
        </div>
      </header>
    );
  };

  if (phase === PHASE.OVERVIEW) {
    return (
      <div className="min-h-screen bg-slate-100">
        <header className="bg-blue-900 text-white">
          <div className="mx-auto max-w-6xl px-6 py-3">
            <p className="text-[11px] uppercase tracking-wider text-blue-200">{exam.toUpperCase()} mock test {form}</p>
          </div>
        </header>
        <main className="mx-auto max-w-3xl px-6 py-12">
          <div className="border border-slate-300 bg-white p-10 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Practice mock · full sitting</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-900">Before you begin</h1>
            <p className="mt-2 leading-7 text-slate-600">
              CELPIP General has four components, taken in order. This sitting runs Listening and Reading
              under exam timing with no review between parts; you complete Writing and Speaking afterward.
            </p>
            <ul className="mt-7 divide-y divide-slate-100 border-y border-slate-100">
              {COMPONENTS.map((c, i) => {
                const Icon = c.icon;
                return (
                  <li key={c.key} className="flex items-center gap-4 py-3.5" data-testid={`overview-component-${c.key}`}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-500"><Icon className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">{i + 1}. {c.label} <span className="font-normal text-slate-400">· {c.parts}</span></p>
                      <p className="text-xs text-slate-500">{c.note}</p>
                    </div>
                    <span className="shrink-0 text-xs tabular-nums text-slate-400">{c.time}</span>
                  </li>
                );
              })}
            </ul>
            <div className="mt-6 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-800">
              Each section is timed and forward-only, just like the real test. Band figures shown afterward are
              practice estimates from your accuracy, not official scores.
            </div>
            <Button size="lg" className="mt-8 w-full bg-blue-900 hover:bg-blue-800" data-testid="mock-overview-begin"
              onClick={() => { setPhase(PHASE.SECTION_INTRO); phaseRef.current = PHASE.SECTION_INTRO; }}>
              Begin test <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (phase === PHASE.SECTION_INTRO) {
    const Icon = SECTION_META[section].icon;
    const meta = COMPONENTS.find((c) => c.key === section);
    return (
      <div className="min-h-screen bg-slate-100">
        <Header showTimer={false} />
        <main className="mx-auto max-w-3xl px-6 py-16">
          <div className="border border-slate-300 bg-white p-10 text-center shadow-sm">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-900">
              <Icon className="h-6 w-6 text-white" />
            </span>
            <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-400">Section {sectionIdx + 1} of {gradedSections.length}</p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">{SECTION_META[section].label} section</h1>
            <p className="mt-2 text-slate-500">
              {sets.length} parts · {meta?.time}. {section === 'listening' ? 'Each recording plays once.' : 'Read each passage, then answer.'} No review between parts; results come at the end of the sitting.
            </p>
            <Button
              size="lg"
              className="mt-8 bg-blue-900 px-10 hover:bg-blue-800"
              data-testid="mock-section-start"
              onClick={() => startSet(sets[0])}
            >
              BEGIN
            </Button>
          </div>
        </main>
      </div>
    );
  }

  if (phase === PHASE.AUDIO) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header showTimer={false} />
        <main className="mx-auto max-w-3xl px-6 py-24">
          <div className="flex flex-col items-center gap-6 border border-slate-300 bg-white p-12 shadow-sm">
            <span className={cn('flex h-14 w-14 items-center justify-center rounded-full', audioFailed ? 'bg-slate-300' : 'bg-blue-900')}>
              <Headphones className={cn('h-6 w-6 text-white', !audioFailed && !autoplayBlocked && 'animate-pulse')} />
            </span>
            {audioFailed ? (
              <>
                <div className="text-center">
                  <p className="font-semibold text-slate-900" data-testid="mock-audio-failed">Audio couldn’t play automatically.</p>
                  <p className="mt-1 max-w-md text-sm text-slate-500">
                    Your browser blocked autoplay or the clip failed to load. Continue to the questions —
                    you can review the transcript on the next screen.
                  </p>
                </div>
                <Button
                  className="bg-blue-900 px-8 hover:bg-blue-800"
                  data-testid="mock-audio-continue"
                  onClick={advanceFromAudio}
                >
                  Continue to questions
                </Button>
              </>
            ) : (
              <>
                <p className="font-semibold text-slate-900">
                  {autoplayBlocked ? 'Tap to start the recording.' : 'Listen carefully. The recording plays only once.'}
                </p>
                {autoplayBlocked && (
                  <p className="max-w-md text-center text-sm text-slate-500">
                    Your browser blocked autoplay. Tap the button to begin — the recording still plays only once.
                  </p>
                )}
                <audio
                  ref={audioElRef}
                  src={audioUrl}
                  data-testid="mock-audio"
                  onEnded={advanceFromAudio}
                  onError={() => setAudioFailed(true)}
                />
                {autoplayBlocked && (
                  <Button
                    onClick={tapToPlay}
                    data-testid="mock-audio-tap-to-play"
                    className="bg-blue-900 px-8 hover:bg-blue-800"
                  >
                    Tap to play
                  </Button>
                )}
              </>
            )}
          </div>
        </main>
      </div>
    );
  }

  if (phase === PHASE.RUN && currentSet) {
    const isListening = section === 'listening';
    const questions = currentSet.questions || [];
    const question = questions[qIdx];
    const answeredCount = questions.filter((q) => picked[`${currentSet.set_id}:${q.qid}`] != null).length;
    // Reading always shows the passage; Listening shows the transcript only
    // when there's no audio slot at all OR the audio just failed to play —
    // that way an unrenderable/blocked recording can never leave the student
    // with a blank listening item.
    const showPassage = !isListening || !currentSet.stimulus?.audio_ref || audioFailed;

    return (
      <div className="min-h-screen bg-slate-100">
        <Header showTimer />
        <main className="mx-auto max-w-6xl px-6 py-8">
          <div className="grid gap-6 lg:grid-cols-2">
            {showPassage && (currentSet.stimulus?.text || currentSet.stimulus?.transcript || currentSet.stimulus?.diagram) && (
              <div>
                <div className="lg:sticky lg:top-6 space-y-4">
                  {currentSet.stimulus?.diagram && <DiagramCard diagram={currentSet.stimulus.diagram} />}
                  {(currentSet.stimulus?.text || currentSet.stimulus?.transcript) && (
                    <div className="border border-slate-300 bg-white shadow-sm">
                      <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
                        <p className="whitespace-pre-wrap text-[15px] leading-7 text-slate-800">
                          {currentSet.stimulus.text || currentSet.stimulus.transcript}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={cn(!showPassage || !(currentSet.stimulus?.text || currentSet.stimulus?.transcript || currentSet.stimulus?.diagram) ? 'lg:col-span-2 mx-auto w-full max-w-3xl' : '', 'space-y-5')}>
              {(isListening ? [question] : questions).filter(Boolean).map((q) => {
                const qNumber = isListening ? qIdx + 1 : questions.indexOf(q) + 1;
                return (
                  <div key={q.qid} className="border border-slate-300 bg-white shadow-sm" data-testid={`mock-question-${currentSet.set_id}-${q.qid}`}>
                    <div className="border-b border-slate-200 px-5 py-3">
                      <p className="text-[15px] font-medium leading-6 text-slate-900">{qNumber}. {q.prompt}</p>
                    </div>
                    <div className="space-y-1 px-3 py-3">
                      {q.options.map((opt, oi) => {
                        const chosen = picked[`${currentSet.set_id}:${q.qid}`] === oi;
                        return (
                          <button
                            key={oi}
                            type="button"
                            data-testid={`mock-option-${q.qid}-${oi}`}
                            onClick={() => pick(currentSet.set_id, q.qid, oi)}
                            className={cn(
                              'flex w-full items-start gap-3 rounded-sm border px-3 py-2 text-left text-sm leading-6 transition-colors',
                              chosen ? 'border-blue-900 bg-blue-50 text-slate-900' : 'border-transparent text-slate-700 hover:bg-slate-50'
                            )}
                          >
                            <span className={cn('mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold', chosen ? 'border-blue-900 bg-blue-900 text-white' : 'border-slate-300 text-slate-400')}>
                              {String.fromCharCode(65 + oi)}
                            </span>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="flex justify-end">
                {isListening ? (
                  <Button
                    size="lg"
                    className="bg-blue-900 px-10 hover:bg-blue-800"
                    disabled={picked[`${currentSet.set_id}:${question?.qid}`] == null}
                    data-testid="mock-next"
                    onClick={() => (qIdx + 1 < questions.length ? setQIdx((i) => i + 1) : advanceSet())}
                  >
                    NEXT
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    className="bg-blue-900 px-10 hover:bg-blue-800"
                    disabled={answeredCount < questions.length}
                    data-testid="mock-next"
                    onClick={advanceSet}
                  >
                    {setIdx + 1 < sets.length ? 'NEXT PART' : 'FINISH SECTION'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // RESULTS
  const writingSets = mock.sections.writing || [];
  const speakingSets = mock.sections.speaking || [];
  return (
    <div className="min-h-screen bg-slate-100">
      <Header showTimer={false} />
      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="text-2xl font-semibold text-slate-900">Mock {mockLabel(form)}: Listening and Reading results</h1>
        <p className="mt-1 text-sm text-slate-500">
          Band figures are practice estimates from your accuracy, for guidance between mocks.
          Official scores come only from the real test.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {gradedSections.map((sec) => {
            const r = sectionResults[sec];
            if (!r) return null;
            const pct = r.n_correct / r.n_total;
            return (
              <div key={sec} className="border border-slate-300 bg-white p-6 shadow-sm" data-testid={`mock-result-${sec}`}>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{SECTION_META[sec].label}</p>
                <p className="mt-2 text-3xl font-semibold tabular-nums text-slate-900">
                  {r.n_correct}<span className="text-slate-300">/{r.n_total}</span>
                </p>
                <p className="mt-1 text-sm text-slate-600">
                  Practice estimate: <span className="font-semibold">CLB {estimateBand(pct)}</span>
                </p>
              </div>
            );
          })}
        </div>

        <div className="mt-8 space-y-3">
          <h2 className="font-semibold text-slate-900">Complete the sitting</h2>
          {writingSets.map((w) => (
            <Link key={w.set_id} to={`/${exam}/write/${w.task_type}?set=${w.set_id}&mock_form=${form}`} className="flex items-center justify-between border border-slate-300 bg-white px-5 py-4 shadow-sm transition-colors hover:bg-slate-50" data-testid={`mock-writing-${w.task_type}`}>
              <span className="text-sm font-medium text-slate-800">Writing: {w.task_type.includes('t1') ? 'Task 1 (Email)' : 'Task 2 (Survey)'}</span>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </Link>
          ))}
          {speakingSets.map((s) => (
            <Link key={s.set_id} to={`/${exam}/speak/${s.task_type}?set=${s.set_id}&mock_form=${form}`} className="flex items-center justify-between border border-slate-300 bg-white px-5 py-4 shadow-sm transition-colors hover:bg-slate-50" data-testid={`mock-speaking-${s.task_type}`}>
              <span className="text-sm font-medium text-slate-800">Speaking: {s.task_type.replace('speaking_', '').replace(/_/g, ' ')}</span>
              <ArrowRight className="h-4 w-4 text-slate-400" />
            </Link>
          ))}
        </div>

        <div className="mt-10 border-t border-slate-200 pt-6">
          <h2 className="font-semibold text-slate-900">Review every question</h2>
          <div className="mt-4 space-y-4">
            {gradedSections.flatMap((sec) => (sectionResults[sec]?.results || []).map((res, i) => (
              <div key={`${res.set_id}-${res.qid}`} className="border border-slate-200 bg-white shadow-sm">
                <div className="flex items-start gap-3 border-b border-slate-100 px-5 py-3">
                  {res.correct ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />}
                  <p className="text-sm font-medium leading-6 text-slate-900">{res.prompt}</p>
                </div>
                {!res.correct && (res.explanation || res.why_yours_attracts) && (
                  <div className="bg-slate-50 px-5 py-3 text-sm leading-6">
                    {res.explanation && <p className="text-slate-700">{res.explanation}</p>}
                    {res.why_yours_attracts && (
                      <p className="mt-1.5 text-slate-600">
                        <span className="font-semibold text-slate-800">Why your answer attracted you: </span>
                        {res.why_yours_attracts}
                      </p>
                    )}
                  </div>
                )}
              </div>
            )))}
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-3 border-t border-slate-200 pt-6">
          <Link to={`/${exam}/mocks`} className="cta">Back to mock tests</Link>
          <Link to="/" className="cta-quiet">Dashboard</Link>
        </div>
      </main>
    </div>
  );
}
